import { useCallback, useEffect, useRef, useState } from "react";

interface ObjectUrlOwner {
	url: string | null;
	setBlob: (blob: Blob) => void;
	clear: () => void;
}

const useObjectUrl = (): ObjectUrlOwner => {
	const [url, setUrl] = useState<string | null>(null);
	const ownedUrl = useRef<string | null>(null);
	const mounted = useRef(true);

	const revoke = useCallback(() => {
		if (!ownedUrl.current) return;
		URL.revokeObjectURL(ownedUrl.current);
		ownedUrl.current = null;
	}, []);

	const setBlob = useCallback(
		(blob: Blob) => {
			const nextUrl = URL.createObjectURL(blob);
			if (!mounted.current) {
				URL.revokeObjectURL(nextUrl);
				return;
			}

			revoke();
			ownedUrl.current = nextUrl;
			setUrl(nextUrl);
		},
		[revoke],
	);

	const clear = useCallback(() => {
		revoke();
		if (mounted.current) setUrl(null);
	}, [revoke]);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			revoke();
		};
	}, [revoke]);

	return { url, setBlob, clear };
};

export default useObjectUrl;
